import { apiClient } from './api';

// No longer need local axios instance or interceptors as we use the central apiClient

export interface DispatchTask {
    _id: string;
    title: string;
    description: string;
    requiredSkills: Array<{
        _id: string;
        name: string;
        category: string;
    }>;
    timeline?: string;
    budgetRange?: {
        min?: number;
        max?: number;
        currency?: string;
    };
    status: 'pending' | 'dispatched' | 'assigned' | 'declined' | 'expired';
    postedBy: {
        _id: string;
        firstName: string;
        lastName: string;
        isEmployerVerified?: boolean;
    };
    assignedTo?: {
        _id: string;
        firstName: string;
        lastName: string;
        isEmployerVerified?: boolean;
    };
    projectId?: string;
    matchedUsers?: Array<{
        userId: string;
        notifiedAt: string;
        respondedAt?: string;
        response?: string;
    }>;
    comments?: TaskComment[];
    applications?: TaskApplication[];
    commentingEnabled?: boolean;
    category?: 'professional' | 'semi-skilled' | 'informal' | 'emergency';
    type?: 'full-time' | 'part-time' | 'contract' | 'gig' | 'one-day' | 'hourly';
    mode?: 'remote' | 'on-site' | 'hybrid' | 'field';
    urgency?: 'immediate' | 'scheduled' | 'flexible';
    location?: {
        city?: string;
        country?: string;
        coordinates?: [number, number]; // [lng, lat]
        address?: string;
    };
    payStructure?: 'fixed' | 'hourly' | 'daily' | 'task-based';
    toolsRequired?: boolean;
    physicalEffort?: 'none' | 'light' | 'moderate' | 'heavy';
    languageRequirements?: string[];
    requiresCV?: boolean;
    expiresAt?: string;
    activeViewers?: string[];
    matchScores?: Array<{
        userId: string;
        score: number;
    }>;
    createdAt: string;
    updatedAt: string;
}

export interface CreateTaskPayload {
    title: string;
    description: string;
    requiredSkills: string[];
    timeline?: string;
    budgetRange?: {
        min?: number;
        max?: number;
        currency?: string;
    };
    category?: 'professional' | 'semi-skilled' | 'informal' | 'emergency';
    type?: 'full-time' | 'part-time' | 'contract' | 'gig' | 'one-day' | 'hourly';
    mode?: 'remote' | 'on-site' | 'hybrid' | 'field';
    payStructure?: 'fixed' | 'hourly' | 'daily' | 'task-based';
    urgency?: 'immediate' | 'scheduled' | 'flexible';
    location?: {
        city?: string;
        country?: string;
        coordinates?: [number, number];
        address?: string;
    };
    toolsRequired?: boolean;
    physicalEffort?: 'none' | 'light' | 'moderate' | 'heavy';
    languageRequirements?: string[];
    requiresCV?: boolean;
    metadata?: Record<string, any>;
}

/**
 * Dispatch Service - API calls for TechUnity Dispatch
 */
class DispatchService {
    /**
     * Create and dispatch a new task
     */
    async createTask(payload: CreateTaskPayload): Promise<{
        success: boolean;
        taskId: string;
        matchCount: number;
        message: string;
    }> {
        return await apiClient.collaborationServiceRequest('/dispatch/tasks', {
            method: 'POST',
            body: JSON.stringify(payload)
        });
    }

    /**
     * Update an existing task
     */
    async updateTask(id: string, payload: CreateTaskPayload): Promise<{
        success: boolean;
        task: DispatchTask;
        message: string;
    }> {
        return await apiClient.collaborationServiceRequest(`/dispatch/tasks/${id}`, {
            method: 'PUT',
            body: JSON.stringify(payload)
        });
    }

    /**
     * Get user's posted tasks
     */
    async getUserTasks(): Promise<{ success: boolean; tasks: DispatchTask[] }> {
        return await apiClient.collaborationServiceRequest('/dispatch/tasks');
    }

    /**
     * Get single task by ID
     */
    async getTaskById(id: string): Promise<{ success: boolean; task: DispatchTask }> {
        return await apiClient.collaborationServiceRequest(`/dispatch/tasks/${id}`);
    }

    /**
     * Get tasks user is matched to
     */
    async getUserMatches(): Promise<{ success: boolean; tasks: DispatchTask[] }> {
        return await apiClient.collaborationServiceRequest('/dispatch/matches');
    }

    /**
     * Get all open tasks (Remote Jobs)
     */
    async getOpenTasks(lat?: number, lng?: number, radius?: number): Promise<{ success: boolean; tasks: DispatchTask[] }> {
        let query = '';
        if (lat && lng) {
            query = `?lat=${lat}&lng=${lng}`;
            if (radius) query += `&radius=${radius}`;
        }
        return await apiClient.collaborationServiceRequest(`/dispatch/open-tasks${query}`);
    }

    /**
     * Accept a dispatched task
     */
    async acceptTask(id: string): Promise<{
        success: boolean;
        message: string;
        task: DispatchTask;
        project: any;
    }> {
        return await apiClient.collaborationServiceRequest(`/dispatch/tasks/${id}/accept`, {
            method: 'POST',
            body: JSON.stringify({ acceptorType: 'user' })
        });
    }

    /**
     * Decline a dispatched task
     */
    async declineTask(id: string, reason?: string): Promise<{
        success: boolean;
        message: string;
    }> {
        return await apiClient.collaborationServiceRequest(`/dispatch/tasks/${id}/decline`, {
            method: 'POST',
            body: JSON.stringify({ reason })
        });
    }

    /**
     * Delete a task
     */
    async deleteTask(id: string): Promise<{ success: boolean; message: string }> {
        return await apiClient.collaborationServiceRequest(`/dispatch/tasks/${id}`, {
            method: 'DELETE'
        });
    }

    /**
     * Update task status (e.g. mark as expired/taken)
     */
    async updateTaskStatus(id: string, status: string): Promise<{ success: boolean; message: string }> {
        return await apiClient.collaborationServiceRequest(`/dispatch/tasks/${id}/status`, {
            method: 'PATCH',
            body: JSON.stringify({ status })
        });
    }

    /**
     * Add a comment to a task
     */
    async addComment(id: string, text: string): Promise<{ success: boolean; comment: TaskComment }> {
        return await apiClient.collaborationServiceRequest(`/dispatch/tasks/${id}/comments`, {
            method: 'POST',
            body: JSON.stringify({ text })
        });
    }

    async applyForTask(id: string, message: string, cvUrl?: string): Promise<{ success: boolean; message: string }> {
        return await apiClient.collaborationServiceRequest(`/dispatch/tasks/${id}/apply`, {
            method: 'POST',
            body: JSON.stringify({ message, cvUrl })
        });
    }

    async getTaskApplications(id: string): Promise<{ success: boolean; applications: TaskApplication[] }> {
        return await apiClient.collaborationServiceRequest(`/dispatch/tasks/${id}/applications`);
    }

    async adjudicateApplication(id: string, appId: string, status: 'accepted' | 'rejected'): Promise<{ success: boolean; message: string }> {
        return await apiClient.collaborationServiceRequest(`/dispatch/tasks/${id}/applications/${appId}`, {
            method: 'PATCH',
            body: JSON.stringify({ status })
        });
    }

    async replyToComment(id: string, commentId: string, text: string): Promise<{ success: boolean; message: string }> {
        return await apiClient.collaborationServiceRequest(`/dispatch/tasks/${id}/comments/${commentId}/reply`, {
            method: 'POST',
            body: JSON.stringify({ text })
        });
    }

    async toggleLikeComment(id: string, commentId: string): Promise<{ success: boolean; likes: number }> {
        return await apiClient.collaborationServiceRequest(`/dispatch/tasks/${id}/comments/${commentId}/like`, {
            method: 'PATCH'
        });
    }

    async deleteComment(id: string, commentId: string): Promise<{ success: boolean; message: string }> {
        return await apiClient.collaborationServiceRequest(`/dispatch/tasks/${id}/comments/${commentId}`, {
            method: 'DELETE'
        });
    }

    async toggleCommenting(id: string, enabled: boolean): Promise<{ success: boolean; message: string }> {
        return await apiClient.collaborationServiceRequest(`/dispatch/tasks/${id}/commenting`, {
            method: 'PATCH',
            body: JSON.stringify({ enabled })
        });
    }

    async getMyApplications(): Promise<{ success: boolean; applications: any[] }> {
        return await apiClient.collaborationServiceRequest('/dispatch/my-applications');
    }

    async getReceivedApplications(): Promise<{ success: boolean; applications: ReceivedApplication[] }> {
        return await apiClient.collaborationServiceRequest('/dispatch/applications');
    }

    /**
     * Report a task for safety/compliance issues
     */
    async reportTask(id: string, reason: string, description: string): Promise<{ success: boolean; message: string }> {
        return await apiClient.collaborationServiceRequest(`/dispatch/tasks/${id}/report`, {
            method: 'POST',
            body: JSON.stringify({ reason, description })
        });
    }

    /**
     * Report a user for misconduct
     */
    async reportUser(userId: string, reason: string, description: string): Promise<{ success: boolean; message: string }> {
        return await apiClient.collaborationServiceRequest(`/dispatch/users/${userId}/report`, {
            method: 'POST',
            body: JSON.stringify({ reason, description })
        });
    }

    /**
     * ADMIN: Get all reported tasks for moderation
     */
    async getReportedTasks(status?: string): Promise<{ success: boolean; tasks: any[] }> {
        const query = status ? `?status=${status}` : '';
        return await apiClient.collaborationServiceRequest(`/dispatch/admin/reported-tasks${query}`);
    }

    /**
     * ADMIN: Moderate a task
     */
    async moderateTask(id: string, action: 'approve' | 'reject' | 'flag' | 'unflag', notes?: string): Promise<{ success: boolean; message: string }> {
        return await apiClient.collaborationServiceRequest(`/dispatch/tasks/${id}/moderate`, {
            method: 'PATCH',
            body: JSON.stringify({ action, notes })
        });
    }

    /**
     * Track user viewing a task for urgency social proof
     */
    async trackViewer(id: string): Promise<{ success: boolean }> {
        return await apiClient.collaborationServiceRequest(`/dispatch/tasks/${id}/view`, {
            method: 'POST'
        });
    }
}

export interface TaskComment {
    _id: string;
    text: string;
    userId: string;
    user?: {
        firstName: string;
        lastName: string;
        avatar?: string;
    };
    createdAt: string;
    likes?: string[];
    replies?: TaskComment[];
}

export interface TaskApplication {
    _id: string;
    applicantId: string;
    message: string;
    cvUrl?: string;
    status: 'pending' | 'accepted' | 'rejected';
    appliedAt: string;
    applicant?: {
        firstName: string;
        lastName: string;
        email: string;
        profile?: { avatar?: string };
        professional?: any;
    };

}

export interface ReceivedApplication {
    _id: string;
    task: {
        _id: string;
        title: string;
        status: string;
    };
    applicant: {
        _id: string;
        firstName: string;
        lastName: string;
        email: string;
        avatar?: string;
        username?: string;
        title?: string;
        location?: string;
        skills?: string[];
    };
    message: string;
    cvUrl?: string;
    status: 'pending' | 'accepted' | 'rejected';
    appliedAt: string;
}

export default new DispatchService();
